<template>
  <div class="institution-module">
    <!-- Modern Tabs Navigation -->
    <div class="tabs-container">
      <div class="tabs-header">
        <div class="tabs-indicator-wrapper">
          <div
            class="tabs-indicator"
            :style="indicatorStyle"
          />
        </div>
        <button
          v-for="(tab, index) in tabs"
          :key="tab.id"
          class="tab-button"
          :class="{ active: activeTab === tab.id }"
          @click="switchTab(tab.id)"
        >
          <span
            class="tab-icon"
            v-html="tab.icon"
          />
          <span class="tab-label">{{ tab.label }}</span>
          <span
            v-if="tab.badge"
            class="tab-badge"
          >{{ tab.badge }}</span>
        </button>
      </div>

      <!-- Tabs Content -->
      <div class="tabs-content">
        <!-- Tab 1: Basic Information -->
        <transition
          name="fade-slide"
          mode="out-in"
        >
          <div
            v-if="activeTab === 'basic-info'"
            class="tab-pane"
          >
            <b-card
              v-if="institution"
              title="Institution Details"
              tag="article"
              class="mb-2 content-wrapper"
            >
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">Institution Name</span>
                  <span class="info-value">{{ institution.name }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Enrolment Code</span>
                  <span class="info-value">{{
                    institution.students_code
                  }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Expiration Date</span>
                  <span class="info-value">{{ institution.expiry_date }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Used Seats</span>
                  <span
                    class="info-value"
                  >{{ institution.used_seats }}/{{
                    institution.total_seats
                  }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">User Maximum Tokens per Year</span>
                  <span class="info-value">{{
                    institution.user_max_token
                  }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Institution Maximum Tokens per Year</span>
                  <span class="info-value">{{
                    institution.institution_max_token
                  }}</span>
                </div>
              </div>
            </b-card>

            <b-card
              title="Users"
              tag="article"
              class="mb-2 content-wrapper"
            >
              <div v-if="studentDetails">
                <div class="student-table-container m-auto p-0">
                  <div class="student-table-body mt-0">
                    <center v-if="isLoading">
                      <b-spinner label="Spinning" />
                    </center>
                    <div
                      v-for="(s, index) in students"
                      :key="index"
                      :class="[
                        'student-row',
                        index % 2 === 0
                          ? 'bg-light-row py-1'
                          : 'bg-dark-row py-1',
                      ]"
                    >
                      <div class="left d-flex align-items-center pl-2">
                        <div class="avatar-box position-relative">
                          <b-avatar
                            :src="s.avatar"
                            size="32"
                          />
                          <span
                            class="status-dot"
                            :class="{
                              'bg-success': s.status === 'online',
                              'bg-danger': s.status === 'offline',
                            }"
                          />
                        </div>
                        <span
                          class="ml-2 text-white-50"
                        >{{ s.fname }} {{ s.lname }}</span>
                      </div>
                      <!-- Roles Section -->
                      <div class="right pr-2">
                        <b-badge
                          v-for="(role, rIndex) in s.roles"
                          :key="rIndex"
                          pill
                          variant="light-primary"
                          class="ml-1"
                        >
                          {{ role }}
                        </b-badge>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="d-flex justify-content-end align-items-center">
                  <b-link
                    :to="{
                      name: 'users',
                      params: { institution_id: institution.id },
                    }"
                    class="mt-1 small text-primary"
                  >View All</b-link>
                </div>
              </div>
              <div v-else="studentDetails">
                <span>No Students</span>
              </div>
            </b-card>
          </div>
        </transition>

        <!-- Tab 2: AI Configuration -->
        <transition
          name="fade-slide"
          mode="out-in"
        >
          <div
            v-if="activeTab === 'ai-config'"
            class="tab-pane"
          >
            <InstitutionAiConfiguration
              :institution-id="id"
              :institution="institution"
            />
          </div>
        </transition>

        <!-- Tab 3: LMS Configurations -->
        <transition
          name="fade-slide"
          mode="out-in"
        >
          <div
            v-if="activeTab === 'lms-config'"
            class="tab-pane"
          >
            <InstitutionLmsConfiguration
              :institution-id="id"
              :institution="institution"
            />
            <b-card
              v-if="institution && institution.lms_enabled == 1"
              title="Activities"
              tag="article"
              class="mb-2 content-wrapper"
            >
              <LMSActivities />
            </b-card>
          </div>
        </transition>

        <!-- Tab 4: Microsoft Configurations -->
        <transition
          name="fade-slide"
          mode="out-in"
        >
          <div
            v-if="activeTab === 'microsoft-config'"
            class="tab-pane"
          >
            <InstitutionMicrosoftConfiguration
              :institution-id="id"
              :institution="institution"
            />
          </div>
        </transition>
      </div>
    </div>

    <!-- Students Card (Now below tabs) -->
    <b-row class="d-flex justify-content-around mt-4">
      <b-col
        lg="12"
        md="12"
        class="content-section"
      />
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getUserData } from '@/auth/utils'
import LMSActivities from '@/views/modules/skeleton/lms/LMSActivities.vue'
import vSelect from 'vue-select'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import {
  BRow,
  BCol,
  BProgress,
  BCard,
  BButton,
  BIcon,
  BImg,
  BCardText,
  BSpinner,
  BAvatar,
  BLink,
  BBadge,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormSelect,
  BFormSelectOption,
  BFormCheckbox,
  BFormRadio,
  BFormRadioGroup,
} from 'bootstrap-vue'
import InstitutionMicrosoftConfiguration from './InstitutionMicrosoftConfiguration.vue'
import InstitutionAiConfiguration from './InstitutionAiConfiguration.vue'
import InstitutionLmsConfiguration from './InstitutionLmsConfiguration.vue'

export default {
  name: 'ConcreteInstitutionPage',
  components: {
    BRow,
    BCol,
    BProgress,
    BButton,
    BIcon,
    BImg,
    BCard,
    BCardText,
    BSpinner,
    BAvatar,
    BLink,
    BBadge,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormSelectOption,
    vSelect,
    LMSActivities,
    InstitutionLmsConfiguration,
    InstitutionMicrosoftConfiguration,
    InstitutionAiConfiguration,
    ValidationObserver,
    ValidationProvider,
    BFormCheckbox,
    BFormRadio,
    BFormRadioGroup,
  },
  computed: {
    ...mapGetters({
      institution: 'institutions/item',
      students: 'institutions/students',
      lmsConfigurations: 'institutions/lmsConfigurations',
    }),
    id() {
      const userData = getUserData()
      return this.$route.params.id ? this.$route.params.id : (userData && userData.institution ? userData.institution.id : null)
    },

    indicatorStyle() {
      const tabIndex = this.tabs.findIndex(tab => tab.id === this.activeTab)
      const width = 100 / this.tabs.length
      const translateX = tabIndex * 100
      return {
        width: `${width}%`,
        transform: `translateX(${translateX}%)`,
      }
    },
  },
  data() {
    return {
      activeTab: 'basic-info',
      tabs: [
        {
          id: 'basic-info',
          label: 'Basic Information',
          icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 16V12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="8" r="1" fill="currentColor"/></svg>',
        },
        {
          id: 'ai-config',
          label: 'AI Configurations',
          icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"xmlns="http://www.w3.org/2000/svg"><rect x="4" y="5" width="16" height="12" rx="3"stroke="currentColor" stroke-width="2"/><line x1="12" y1="3" x2="12" y2="5"stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="9" cy="10" r="1" fill="currentColor"/><circle cx="15" cy="10" r="1" fill="currentColor"/><path d="M10 14H14"stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
        },
        {
          id: 'lms-config',
          label: 'LMS Configurations',
          icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5L2 10L12 15L22 10L12 5Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M6 12V18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M18 12V18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 15V21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
        },
        {
          id: 'microsoft-config',
          label: 'Microsoft Configurations',
          icon: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1H11V11H1V1Z" fill="currentColor"/><path d="M13 1H23V11H13V1Z" fill="currentColor"/><path d="M1 13H11V23H1V13Z" fill="currentColor"/><path d="M13 13H23V23H13V13Z" fill="currentColor"/></svg>',
        },
      ],
      studentDetails: [],
      isLoading: false,
    }
  },
  mounted() {
    this.init()
    if (this.$route.query.tab) {
      this.activeTab = this.$route.query.tab
    }
  },
  methods: {
    switchTab(tabId) {
      this.activeTab = tabId
    },
    init() {
      this.isLoading = true

      this.$store.dispatch('institutions/students', {
        institution_id: this.id,
      })
      this.$store.dispatch('institutions/get', this.id).then(response => {
        this.isLoading = false
      })
    },

    openInstitution(id) {
      if (id) {
      } else {
        console.log('No ID provided in the URL.')
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.institution-module {
  padding: 2rem 1rem;
  animation: fadeIn 1s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .tabs-container {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 16px;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    overflow: hidden;
    margin-bottom: 2rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }

  .tabs-header {
    display: flex;
    position: relative;
    background: #000a1e;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .tabs-indicator-wrapper {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    overflow: hidden;
  }

  .tabs-indicator {
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(133, 13, 204, 1) 0%,
      rgba(15, 217, 222, 1) 100%
    );
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 3px 3px 0 0;
  }

  .tab-button {
    flex: 1;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    padding: 1.25rem 1.5rem;
    font-size: 0.95rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    position: relative;
    overflow: hidden;

    &:hover {
      background: rgba(255, 255, 255, 0.05);
      color: rgba(255, 255, 255, 0.9);
    }

    &.active {
      color: #fff;
      background: rgba(255, 255, 255, 0.08);

      .tab-icon svg {
        color: #0fd9de;
        filter: drop-shadow(0 0 8px rgba(15, 217, 222, 0.3));
      }
    }
  }

  .tab-icon {
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      transition: all 0.3s ease;
      color: rgba(255, 255, 255, 0.7);
    }
  }

  .tab-label {
    letter-spacing: 0.3px;
  }

  .tab-badge {
    background: linear-gradient(90deg, #ff6b6b, #ffa726);
    color: white;
    font-size: 0.7rem;
    padding: 2px 8px;
    border-radius: 12px;
    font-weight: 600;
  }

  .tabs-content {
    padding: 2rem;
  }

  .tab-pane {
    animation: fadeIn 0.4s ease;
  }

  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: all 0.3s ease;
  }

  .fade-slide-enter,
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(10px);
  }

  .content-wrapper {
    background: #000a1e;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    backdrop-filter: blur(10px);
    color: #e9ecef;
    animation: slideInLeft 0.5s ease forwards;

    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-30px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(480px, 1fr));
    gap: 1.5rem;
    margin-top: 1rem;
  }

  .info-item {
    background: rgba(255, 255, 255, 0.05);
    padding: 1rem;
    border-radius: 10px;
    border-left: 4px solid rgba(15, 217, 222, 0.5);
    transition: transform 0.3s ease, border-color 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      border-left-color: rgba(133, 13, 204, 1);
      background: rgba(255, 255, 255, 0.08);
    }
  }

  .info-label {
    display: block;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .info-value {
    display: block;
    font-size: 1.1rem;
    color: #fff;
    font-weight: 500;
    word-break: break-word;
  }

  .masked {
    font-family: "Courier New", monospace;
    background: rgba(0, 0, 0, 0.2);
    padding: 2px 8px;
    border-radius: 4px;
    display: inline-block;
  }

  .url-link {
    color: #0fd9de !important;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #860dcc !important;
      text-decoration: underline;
    }
  }

  .ai-config-form {
    .modern-input {
      background: rgba(255, 255, 255, 0.05) !important;
      border: 1px solid rgba(255, 255, 255, 0.2) !important;
      color: #fff !important;
      border-radius: 8px;
      padding: 0.75rem 1rem;
      transition: all 0.3s ease;

      &:focus {
        background: rgba(255, 255, 255, 0.08) !important;
        border-color: rgba(15, 217, 222, 0.5) !important;
        box-shadow: 0 0 0 3px rgba(15, 217, 222, 0.1) !important;
      }

      &::placeholder {
        color: rgba(255, 255, 255, 0.4) !important;
      }
    }

    .form-hint {
      display: block;
      margin-top: 0.5rem;
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.85rem;
    }
  }

  .form-actions {
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .submit-btn {
    background: linear-gradient(
      90deg,
      rgba(133, 13, 204, 1) 0%,
      rgba(15, 217, 222, 1) 100%
    ) !important;
    border: none;
    padding: 0.75rem 2rem;
    border-radius: 8px;
    font-weight: 600;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(133, 13, 204, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .reset-btn {
    background: transparent !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    color: rgba(255, 255, 255, 0.8) !important;
    padding: 0.75rem 2rem;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1) !important;
      border-color: rgba(255, 255, 255, 0.5) !important;
      color: #fff !important;
    }
  }

  .empty-state {
    text-align: center;
    padding: 3rem 1rem;
  }

  .empty-icon {
    margin-bottom: 1.5rem;
    color: rgba(255, 255, 255, 0.3);
  }

  .empty-title {
    color: #fff;
    margin-bottom: 0.75rem;
    font-size: 1.5rem;
  }

  .empty-description {
    color: rgba(255, 255, 255, 0.6);
    max-width: 400px;
    margin: 0 auto 2rem;
    line-height: 1.6;
  }

  .empty-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;

    .action-btn {
      background: rgba(255, 255, 255, 0.05) !important;
      border: 1px solid rgba(255, 255, 255, 0.2) !important;
      color: rgba(255, 255, 255, 0.8) !important;
      padding: 0.75rem 1.5rem;
      border-radius: 8px;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.1) !important;
        border-color: rgba(15, 217, 222, 0.5) !important;
        color: #fff !important;
      }
    }
  }

  .student-table-container {
    max-width: 90%;
    border-radius: 14px;
    overflow: hidden;
    backdrop-filter: blur(18px);
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }

  .student-table-body {
    max-height: 500px;
    overflow-y: auto;
  }

  .student-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 12px;
    transition: background 0.3s;
  }

  .bg-light-row {
    background-color: rgba(255, 255, 255, 0.07);
  }

  .bg-dark-row {
    background-color: rgba(255, 255, 255, 0.03);
  }

  .avatar-box {
    position: relative;
  }

  .status-dot {
    position: absolute;
    bottom: -1px;
    right: -1px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: 1px solid #1f1f1f;
  }

  @media (max-width: 768px) {
    .tabs-header {
      flex-direction: column;
    }

    .tab-button {
      padding: 1rem;
      justify-content: flex-start;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .form-actions {
      flex-direction: column;
    }

    .submit-btn,
    .reset-btn {
      width: 100%;
    }
  }
}
</style>
